import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { CheckCircle, Clock, XCircle, AlertTriangle, Loader2 } from 'lucide-react';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// ── Stripe redirect landing page (read-only status display) ──────────────────
//
// A customer who pays an invoice with a redirect-based method (Klarna, some
// wallets/bank redirects) leaves the app and is sent here by Stripe afterward.
// This page ONLY reads and shows the outcome — it never records a payment,
// never writes, and never reads the invoice doc. The ledger is already recorded
// server-side by the payment_intent.succeeded webhook (Seam 2b); Stripe's
// PaymentIntent status is the authoritative "did the money move" signal, so we
// display from that directly and are correct even if the webhook write lands a
// moment later.
//
// The invoice PaymentIntent is a DIRECT charge on the connected account, so the
// Stripe.js instance must be scoped with { stripeAccount } to retrieve it — the
// account id rides in on ?acct= (mirrors how PaymentModal loads Stripe for the
// Elements form). Stripe appends payment_intent_client_secret / redirect_status
// on return.
//
// Public route (no RequireAuth, not inside CustomerPortal's session gate): it
// needs no Firebase/portal session — only the publishable key and the client
// secret the payer legitimately holds — so a redirect that drops the session
// still shows a correct result.

const fmtMoney = (amount, currency) => {
    try {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: (currency || 'CAD').toUpperCase(),
        }).format(amount);
    } catch (_) {
        // Unknown currency code — fall back to a bare 2-decimal amount.
        return `$${Number(amount).toFixed(2)}`;
    }
};

export default function PortalPaymentResult() {
    const [searchParams] = useSearchParams();
    // phase: 'loading' | 'succeeded' | 'processing' | 'failed' | 'unknown'
    const [result, setResult] = useState({ phase: 'loading' });

    useEffect(() => {
        let cancelled = false;

        const clientSecret = searchParams.get('payment_intent_client_secret');
        const acct = searchParams.get('acct');

        // No client secret to confirm (or Stripe not configured) → graceful
        // neutral state rather than a crash/white screen.
        if (!clientSecret || !stripeKey) {
            setResult({ phase: 'unknown' });
            return;
        }

        (async () => {
            try {
                const stripe = await loadStripe(
                    stripeKey,
                    acct ? { stripeAccount: acct } : undefined
                );
                if (!stripe) {
                    if (!cancelled) setResult({ phase: 'unknown' });
                    return;
                }

                const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);
                if (cancelled) return;

                if (error || !paymentIntent) {
                    setResult({ phase: 'unknown' });
                    return;
                }

                const amount = Number.isFinite(paymentIntent.amount)
                    ? paymentIntent.amount / 100
                    : null;
                const currency = paymentIntent.currency || 'cad';

                switch (paymentIntent.status) {
                    case 'succeeded':
                        setResult({ phase: 'succeeded', amount, currency });
                        break;
                    case 'processing':
                        setResult({ phase: 'processing', amount, currency });
                        break;
                    case 'requires_payment_method':
                    case 'requires_action':
                    case 'requires_confirmation':
                    case 'canceled':
                        setResult({ phase: 'failed' });
                        break;
                    default:
                        setResult({ phase: 'unknown' });
                }
            } catch (_e) {
                if (!cancelled) setResult({ phase: 'unknown' });
            }
        })();

        return () => { cancelled = true; };
    }, [searchParams]);

    const views = {
        loading: {
            icon: <Loader2 size={48} className="animate-spin text-[#5F4EC9]" />,
            title: 'Confirming your payment…',
            body: 'One moment while we check the status with our payment processor.',
            close: false,
        },
        succeeded: {
            icon: <CheckCircle size={48} className="text-green-600" />,
            title: result.amount != null
                ? `Payment successful — ${fmtMoney(result.amount, result.currency)} paid`
                : 'Payment successful',
            body: 'Thank you — your payment went through. You can safely close this tab.',
            close: true,
        },
        processing: {
            icon: <Clock size={48} className="text-amber-500" />,
            title: 'Payment processing',
            body: 'Your payment is still completing. No further action is needed — you can safely close this tab.',
            close: true,
        },
        failed: {
            icon: <XCircle size={48} className="text-red-600" />,
            title: 'Payment didn’t complete',
            body: 'Your payment was not completed. Please return to the portal and try again.',
            close: true,
        },
        unknown: {
            icon: <AlertTriangle size={48} className="text-gray-400" />,
            title: 'Couldn’t confirm your payment',
            body: 'We couldn’t confirm your payment status here. Please check your invoices in the portal, or contact us if you’re unsure — you can safely close this tab.',
            close: true,
        },
    };

    const view = views[result.phase] || views.unknown;

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center">
                <div className="flex justify-center mb-4">{view.icon}</div>
                <h1 className="text-xl font-bold text-[#12192D] mb-2">{view.title}</h1>
                <p className="text-gray-600 text-sm leading-relaxed">{view.body}</p>
            </div>
        </div>
    );
}
